import { useState, useLayoutEffect, useCallback, useEffect, type RefObject } from 'react';

const DEFAULT_MAX_HEIGHT = 400; // Default max height before truncation

export interface UseContentOverflowPayload {
    readonly contentRef: RefObject<HTMLElement | null>;
    readonly contentId: string;
    readonly enableExpend?: boolean;
    readonly maxHeight?: number;
}

export function useContentOverflow(payload: UseContentOverflowPayload) {
    const { contentRef, contentId, enableExpend = false, maxHeight = DEFAULT_MAX_HEIGHT } = payload;
    const [isExpended, setIsExpended] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);

    useLayoutEffect(() => {
        // Early return if not enabled, already expended, or ref not available
        if (!enableExpend || isExpended || !contentRef.current) {
            setShowReadMore(false);
            return;
        }

        const element = contentRef.current;
        const animationFrameId = requestAnimationFrame(() => {
            const isOverflowing = element.scrollHeight > element.clientHeight && element.scrollHeight > maxHeight;
            setShowReadMore(isOverflowing);
        });

        return () => cancelAnimationFrame(animationFrameId);
    }, [contentRef, contentId, enableExpend, isExpended, maxHeight]);

    const expendContent = useCallback(() => {
        if (enableExpend) setIsExpended(true);
    }, [enableExpend]);

    // Reset expended state if content changes
    useEffect(() => {
        setIsExpended(false);
    }, [contentId]);

    return {
        isExpended,
        showReadMore,
        expendContent,
        isTruncatable: !!enableExpend && !isExpended,
    };
}
